import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, Music4, Clapperboard, Film, Megaphone, Star, ArrowLeft, Loader2, Sparkles, Trash2 } from "lucide-react";
import api, { publicMediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const CAT_META = {
  video: { label: "Videos", icon: Film },
  cancion: { label: "Canciones", icon: Music4 },
  corto: { label: "Cortometrajes", icon: Clapperboard },
  anuncio: { label: "Anuncios", icon: Megaphone },
};

function Thumb({ item }) {
  const poster = item.poster ? publicMediaUrl(item.poster) : null;
  const Icon = (CAT_META[item.category] || CAT_META.video).icon;
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-[#12131C]">
      {poster ? (
        <img src={poster} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center"><Icon className="w-8 h-8 text-white/25" /></div>
      )}
      <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity">
        <div className="w-11 h-11 rounded-full bg-[#FF334B] flex items-center justify-center"><Play className="w-5 h-5 text-white ml-0.5" /></div>
      </div>
      {item.featured && (
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#FF9F2E] text-black">
          <Star className="w-3 h-3" /> Destacado
        </span>
      )}
    </div>
  );
}

function Card({ item, isAdmin, onAdmin }) {
  const navigate = useNavigate();
  const Icon = (CAT_META[item.category] || CAT_META.video).icon;
  return (
    <div className="group" data-testid={`community-card-${item.id}`}>
      <button onClick={() => navigate(`/comunidad/${item.id}`)} className="block w-full text-left">
        <Thumb item={item} />
        <div className="mt-2.5">
          <div className="font-head font-semibold text-sm truncate">{item.title}</div>
          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-[#64748B]">
            <span className="truncate">{item.creator_name}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1 text-[#94A3B8]"><Icon className="w-3 h-3" />{(CAT_META[item.category] || {}).label?.replace(/s$/, "") || item.category}</span>
          </div>
        </div>
      </button>
      {isAdmin && (
        <div className="flex gap-2 mt-2">
          <button data-testid={`admin-feature-${item.id}`} onClick={() => onAdmin(item, item.featured ? "unfeature" : "feature")}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 inline-flex items-center gap-1">
            <Star className="w-3 h-3" /> {item.featured ? "Quitar destacado" : "Destacar"}
          </button>
          <button data-testid={`admin-remove-${item.id}`} onClick={() => onAdmin(item, "remove")}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[#FF334B]/50 text-[#FF334B] hover:bg-[#FF334B]/10 inline-flex items-center gap-1">
            <Trash2 className="w-3 h-3" /> Retirar
          </button>
        </div>
      )}
    </div>
  );
}

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, feat] = await Promise.all([
        api.get(`/community${cat !== "all" ? `?category=${cat}` : ""}`),
        api.get("/community/featured"),
      ]);
      setItems(list.data.items);
      setCats(list.data.categories);
      setFeatured(feat.data.items);
    } catch { /* noop */ }
    setLoading(false);
  }, [cat]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (user) api.get("/me/admin").then((r) => setIsAdmin(r.data.admin)).catch(() => {});
  }, [user]);

  const onAdmin = async (item, action) => {
    try {
      await api.post(`/admin/community/${item.id}/${action}`);
      toast.success(action === "remove" ? "Publicación retirada" : action === "feature" ? "Destacado ✨" : "Quitado de destacados");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Acción no permitida"); }
  };

  return (
    <div className="min-h-screen grain relative overflow-hidden pb-24" data-testid="community-page">
      <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(255,51,75,0.20), transparent 70%)" }} />

      <header className="relative z-10 flex items-center justify-between px-5 sm:px-10 py-6">
        <Logo />
        <div className="flex items-center gap-2">
          {user ? (
            <button onClick={() => navigate("/dashboard")} className="text-sm font-semibold px-4 py-2 rounded-full border border-white/15 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Al estudio
            </button>
          ) : (
            <button data-testid="community-cta-signin" onClick={() => navigate("/")} className="text-sm font-semibold px-5 py-2 rounded-full bg-[#FF334B] text-white hover:bg-[#E0223A] transition-colors inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Crear el mío
            </button>
          )}
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-5 sm:px-10">
        <div className="mb-8">
          <div className="mono-label mb-3">Comunidad Metáfora</div>
          <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight">Hecho por la <span className="text-[#FF334B]">comunidad</span></h1>
          <p className="mt-3 text-base text-[#94A3B8] max-w-2xl">Videos, canciones y cortometrajes creados con IA por creadores como tú. Explora, reproduce y comparte.</p>
        </div>

        {featured.length > 0 && (
          <section className="mb-12" data-testid="community-featured">
            <div className="flex items-center gap-2 mb-4"><Star className="w-5 h-5 text-[#FF9F2E]" /><h2 className="font-head text-xl font-bold">Destacados</h2><span className="text-xs text-[#64748B]">· selección oficial</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featured.map((it) => <Card key={it.id} item={it} isAdmin={isAdmin} onAdmin={onAdmin} />)}
            </div>
          </section>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="community-filters">
          <button onClick={() => setCat("all")} data-testid="filter-all"
            className={`text-sm font-semibold px-4 py-1.5 rounded-full transition-colors ${cat === "all" ? "bg-[#FF334B] text-white" : "border border-white/12 text-[#94A3B8] hover:border-white/30"}`}>Todo</button>
          {cats.map((c) => {
            const Icon = (CAT_META[c] || CAT_META.video).icon;
            return (
              <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c}`}
                className={`text-sm font-semibold px-4 py-1.5 rounded-full transition-colors inline-flex items-center gap-1.5 ${cat === c ? "bg-[#FF334B] text-white" : "border border-white/12 text-[#94A3B8] hover:border-white/30"}`}>
                <Icon className="w-3.5 h-3.5" /> {(CAT_META[c] || {}).label || c}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-[#FF334B] spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-[#64748B]" data-testid="community-empty">
            <Film className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Aún no hay publicaciones en esta categoría. ¡Sé el primero en publicar!
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="community-grid">
            {items.map((it) => <Card key={it.id} item={it} isAdmin={isAdmin} onAdmin={onAdmin} />)}
          </div>
        )}
      </main>
    </div>
  );
}
